import { createContext, useContext } from 'react';

import { css, keyframes } from '@emotion/css';

import { color } from '#components/mobile/ui/tokens';

/**
 * Icono de «pagada»: ✓ en un círculo. Sin pagar, solo el contorno gris; pagada
 * (detectada o marcada), círculo verde con el ✓ blanco. Al marcarse (o
 * detectarse por primera vez) el ✓ se dibuja con el trazo y el círculo da un
 * pequeño bote de sello (~0,9 s); con «reducir movimiento», quieto.
 */

/** Duración del trazo + sello. */
export const DURACION_SELLO = 900;
/** Tiempo que una recién pagada cuenta como «reciente» (se anima). */
export const DURACION_PAGADA = DURACION_SELLO + 100;

const LARGO_CHECK = 14;

const trazar = keyframes({
  '0%, 15%': { strokeDashoffset: LARGO_CHECK },
  '60%, 100%': { strokeDashoffset: 0 },
});

const sellar = keyframes({
  '0%': { transform: 'scale(0.6)', opacity: 0.4 },
  '35%': { transform: 'scale(1)', opacity: 1 },
  '60%': { transform: 'scale(1.3)' },
  '78%': { transform: 'scale(0.9)' },
  '90%': { transform: 'scale(1.06)' },
  '100%': { transform: 'scale(1)', opacity: 1 },
});

const quieto = css({
  '@media (prefers-reduced-motion: reduce)': {
    '& *': { animation: 'none !important' },
  },
});

export function CheckPagada({
  size = 16,
  pagada = true,
  animar = false,
}: {
  size?: number;
  /** Sin pagar: contorno gris. */
  pagada?: boolean;
  animar?: boolean;
}) {
  const ms = `${DURACION_SELLO}ms`;
  return (
    <svg
      viewBox="0 0 20 20"
      width={size}
      height={size}
      aria-hidden
      data-pagada={pagada || undefined}
      className={quieto}
      style={{ flexShrink: 0, overflow: 'visible' }}
    >
      <g
        style={{
          transformBox: 'fill-box',
          transformOrigin: '50% 50%',
          animation:
            animar && pagada
              ? `${sellar} ${ms} cubic-bezier(.3,.7,.3,1) both`
              : undefined,
        }}
      >
        <circle
          cx={10}
          cy={10}
          r={8.6}
          fill={pagada ? color.ok : 'none'}
          stroke={pagada ? color.ok : color.fg2}
          strokeWidth={1.6}
          // Sin pagar, contraste ≥ 3:1 con el fondo (B4 · H31).
          opacity={1}
        />
        {pagada && (
          <path
            d="M6 10.4l2.7 2.7L14.2 7.5"
            fill="none"
            stroke="#fff"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={LARGO_CHECK}
            style={{
              animation: animar ? `${trazar} ${ms} ease-out both` : undefined,
            }}
          />
        )}
      </g>
    </svg>
  );
}

/** Pagadas del mes visto y las que se acaban de pagar (se animan). */
export const PagadasContext = createContext<{
  pagadas: ReadonlySet<string>;
  recientes: ReadonlySet<string>;
}>({ pagadas: new Set(), recientes: new Set() });

export function usePagadaFila(id: string | undefined) {
  const { pagadas, recientes } = useContext(PagadasContext);
  const pagada = id != null && pagadas.has(id);
  const reciente = id != null && recientes.has(id);
  return { pagada, reciente };
}
